(() => {
  const existingRuntime = window.FourPawTabsRuntime;
  if (existingRuntime) {
    existingRuntime.initWithin(document);
    return;
  }

  const instances = new WeakMap();

  class FourPawTabs {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[data-fp-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-fp-panel]'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.nearViewport = false;
      this.inViewport = false;
      this.loadObserver = null;
      this.playbackObserver = null;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onVideoStateChange = this.onVideoStateChange.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      root.addEventListener('click', this.onClick);
      root.addEventListener('keydown', this.onKeydown);
      root.addEventListener('play', this.onVideoStateChange, true);
      root.addEventListener('pause', this.onVideoStateChange, true);
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.observeViewport();
      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      if (selected) this.activate(selected);
    }

    panelFor(tab) {
      const panelId = tab.getAttribute('aria-controls');
      return this.panels.find((panel) => panel.id === panelId);
    }

    activePanel() {
      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      return selected ? this.panelFor(selected) : null;
    }

    tabForBlock(blockId) {
      return this.tabs.find((tab) => tab.dataset.blockId === blockId);
    }

    observeViewport() {
      if (!('IntersectionObserver' in window)) {
        this.nearViewport = true;
        this.inViewport = true;
        return;
      }

      this.loadObserver = new IntersectionObserver(([entry]) => {
        this.nearViewport = entry.isIntersecting;
        if (this.nearViewport) this.syncVideos(this.activePanel());
      }, { rootMargin: '320px 0px' });
      this.playbackObserver = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        this.syncVideos(this.activePanel());
      }, { threshold: 0.15 });
      this.loadObserver.observe(this.root);
      this.playbackObserver.observe(this.root);
    }

    hydrateVideo(video) {
      if (video.dataset.loaded === 'true') return;
      let hydrated = false;
      if (video.dataset.poster && !video.getAttribute('poster')) {
        video.setAttribute('poster', video.dataset.poster);
        video.removeAttribute('data-poster');
        hydrated = true;
      }
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.getAttribute('src') && source.dataset.src) {
          source.src = source.dataset.src;
          hydrated = true;
        }
      });
      if (!hydrated) return;
      video.dataset.loaded = 'true';
      video.load();
    }

    activate(nextTab, options = {}) {
      if (!nextTab || !this.tabs.includes(nextTab)) return;

      this.tabs.forEach((tab) => {
        const active = tab === nextTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      const activePanel = this.panelFor(nextTab);
      this.panels.forEach((panel) => {
        const active = panel === activePanel;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        panel.toggleAttribute('inert', !active);
      });

      this.syncVideos(activePanel);
      if (options.focus) nextTab.focus();
    }

    syncVideos(activePanel) {
      this.panels.forEach((panel) => {
        panel.querySelectorAll('[data-fp-tab-video]').forEach((video) => {
          const active = panel === activePanel;
          if (active && this.nearViewport) this.hydrateVideo(video);

          const canAutoplay = active
            && this.inViewport
            && document.visibilityState === 'visible'
            && !this.reducedMotion.matches
            && video.dataset.autoplay === 'true'
            && video.dataset.userPaused !== 'true';
          if (!canAutoplay) {
            video.pause();
            this.updateVideoToggle(video);
            return;
          }

          const playAttempt = video.play();
          if (playAttempt) playAttempt.catch(() => this.updateVideoToggle(video));
        });
      });
    }

    updateVideoToggle(video) {
      const media = video.closest('.fp-tabs__media');
      const toggle = media && media.querySelector('[data-fp-video-toggle]');
      if (!toggle) return;

      const playing = !video.paused && !video.ended;
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.setAttribute(
        'aria-label',
        playing ? toggle.dataset.pauseLabel : toggle.dataset.playLabel
      );
    }

    onClick(event) {
      const tab = event.target.closest('[data-fp-tab]');
      if (tab && this.root.contains(tab)) {
        this.activate(tab);
        return;
      }

      const toggle = event.target.closest('[data-fp-video-toggle]');
      if (!toggle || !this.root.contains(toggle)) return;

      const video = toggle.closest('.fp-tabs__media').querySelector('[data-fp-tab-video]');
      if (!video) return;
      if (video.paused) {
        video.dataset.userPaused = 'false';
        this.hydrateVideo(video);
        const playAttempt = video.play();
        if (playAttempt) playAttempt.catch(() => this.updateVideoToggle(video));
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
      }
    }

    onKeydown(event) {
      const currentTab = event.target.closest('[data-fp-tab]');
      if (!currentTab || !this.root.contains(currentTab)) return;

      const currentIndex = this.tabs.indexOf(currentTab);
      const rtl = getComputedStyle(this.root).direction === 'rtl';
      let nextIndex;

      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (event.key === 'ArrowRight') nextIndex = currentIndex + (rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') nextIndex = currentIndex + (rtl ? 1 : -1);
      if (event.key === 'ArrowDown') nextIndex = currentIndex + 1;
      if (event.key === 'ArrowUp') nextIndex = currentIndex - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      nextIndex = (nextIndex + this.tabs.length) % this.tabs.length;
      this.activate(this.tabs[nextIndex], { focus: true });
    }

    onVideoStateChange(event) {
      if (event.target.matches('[data-fp-tab-video]')) {
        this.updateVideoToggle(event.target);
      }
    }

    onMotionPreferenceChange() {
      this.syncVideos(this.activePanel());
    }

    onVisibilityChange() {
      this.syncVideos(this.activePanel());
    }

    destroy() {
      this.loadObserver?.disconnect();
      this.playbackObserver?.disconnect();
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.root.removeEventListener('play', this.onVideoStateChange, true);
      this.root.removeEventListener('pause', this.onVideoStateChange, true);
      this.root.querySelectorAll('[data-fp-tab-video]').forEach((video) => video.pause());
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container.matches?.('[data-fp-tabs]')) roots.push(container);
    container.querySelectorAll?.('[data-fp-tabs]').forEach((root) => roots.push(root));
    return roots;
  };

  const runtime = {
    initWithin(container) {
      rootsWithin(container).forEach((root) => {
        if (!instances.has(root)) instances.set(root, new FourPawTabs(root));
      });
    },

    destroyWithin(container) {
      rootsWithin(container).forEach((root) => {
        const instance = instances.get(root);
        if (!instance) return;
        instance.destroy();
        instances.delete(root);
      });
    },

    selectBlock(event) {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;

      const root = Array.from(document.querySelectorAll('[data-fp-tabs]')).find((candidate) => {
        const sectionMatches = !event.detail.sectionId
          || candidate.dataset.sectionId === event.detail.sectionId;
        return sectionMatches && candidate.querySelector(`[data-block-id="${blockId}"]`);
      });
      if (!root) return;

      const instance = instances.get(root);
      const tab = instance && instance.tabForBlock(blockId);
      if (tab) instance.activate(tab, { focus: false });
    }
  };

  window.FourPawTabsRuntime = runtime;
  runtime.initWithin(document);

  document.addEventListener('shopify:section:load', (event) => runtime.initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => runtime.destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => runtime.selectBlock(event));
})();
